import React, { useEffect, useState } from "react";
import  { useDispatch, useSelector } from "react-redux";
import { Blade, Button, ReduxControls, ProgressIndicator, ErrorBanner } from "rhombus-react";
import { reduxForm, Field, getFormValues, change } from 'redux-form';

import DuplicateSuffixField from './DuplicateSuffixField';
import useActiveCreative from "hooks/useActiveCreative";
import { conceptNameAllowedChars, conceptNameMaxLength, CONCEPT_NAME_MAX_LENGTH } from 'widgets/forms/validators/Concepts';
import { required } from "widgets/forms/validators/validators";
import { isConceptDynamic, isSocialCreative } from 'utils/common';
import { closeModal } from 'reducers/Modal';
import { loadCreatives } from 'reducers/Creatives';
import { duplicateConcept } from 'reducers/Concepts';
import { loadConcept } from "reducers/ConceptDetails";

const DUPLICATE_CONCEPTS_FORM = "duplicate-creatives-form";

import "./DuplicateCreatives.css";

export function defaultNewName(concept, suffix = ' copy') {
	return `${concept?.name || ''}${suffix}`;
}

function getConcepts(creatives) {
	let conceptsById = {};
	creatives.forEach((creative) => {
		conceptsById[creative.id] = {
			id: creative.conceptId,
			name: creative.conceptName,
		};
	});
	return Object.values(conceptsById);
}

function DuplicateConcepts(props){
	let dispatch = useDispatch()
		, { creatives, libraryId, creativeId, handleSubmit, invalid } = props
		, [errorMessages, setErrorMessages] = useState([])
		, [apiError, setApiError] = useState(false)
		, concepts = getConcepts(creatives)
		, isSingle = concepts.length === 1
		, modalTitle = 'Duplicate Concept'
		, { isUpdating, isLoadingConcept, conceptDetails, formValues } = useSelector(state => ({
			isUpdating: state.concepts.isSaving,
			isLoadingConcept: !state.conceptDetails.isLoaded && isSingle, //we only care about this in single mode
			conceptDetails: state.conceptDetails.data,
			formValues: getFormValues(DUPLICATE_CONCEPTS_FORM)(state),
		}))
		, { newConceptName, shouldCopyVersions, suffix }	= formValues || {}
		, onClose = () => dispatch(closeModal())
		, afterSubmit = (response) => {
			if (response?.success) {
				dispatch(closeModal());
				return dispatch(loadCreatives(libraryId));
			} else {
				setApiError(true);

				const { description = "" } = response?.payload?.errorObj || {};
				setErrorMessages([description]);
			}
		}
		, displayVersions = isSingle && !isLoadingConcept && !isSocialCreative(creatives[0]) && isConceptDynamic(conceptDetails)
		, onSubmit = () => {
			if (isSingle) {
				let submitData = {
					copyVersions: shouldCopyVersions,
					conceptId: concepts[0].id,
					libraryId,
					newConceptName,
				};
				return dispatch(duplicateConcept(submitData)).then(afterSubmit);
			} else {
				// let conceptData = concepts.map((concept, i) => ({
				// 	id: concept.id,
				// 	newName: formValues.concepts[i].newName,
				// }));
				// let submitData = {
				// 	libraryId,
				// 	concepts: conceptData,
				// };
				return;
				// console.log('bulk submitData', submitData);
				//todo: an endpoint for this does not exist
			}
		}
		, updateConceptSuffixes = (suffixStr = ' copy') => {
			concepts.forEach((concept, i) => {
				let newName = defaultNewName(concept, suffixStr);
				dispatch(change(DUPLICATE_CONCEPTS_FORM, `concepts[${i}].newName`, newName));
			});
		}
		, setSuffix = () => updateConceptSuffixes(suffix)
		;

	useActiveCreative(creativeId);

	//init default duplicate names, load single concept details (if in single mode)
	useEffect(() => {
		if (isSingle) {
			let newName = defaultNewName(concepts[0]);
			dispatch(loadConcept(libraryId, concepts[0].id));
			dispatch(change(DUPLICATE_CONCEPTS_FORM, 'newConceptName', newName));
		} else {
			dispatch(change(DUPLICATE_CONCEPTS_FORM, 'suffix', ' copy'));
			updateConceptSuffixes(' copy');
		}
	}, []);

	if (isSingle) {
		modalTitle = `Duplicate Concept - ${concepts[0].name}`;
	} else {
		modalTitle = `Duplicate Concepts - ${concepts.length} Concepts`;
	}

	const renderContentSingle = () => (
		<div className="duplicate-creative-content single-duplicate">
			<ProgressIndicator isLoading={isUpdating || isLoadingConcept} isOpaque={isLoadingConcept} />
			{!!errorMessages.length && <ErrorBanner messages={errorMessages} title="Error! Could Not Duplicate Concept" type="error" />}
			<Field
				name="newConceptName"
				label="Name for Duplicated Concept"
				required={true}
				charMax={CONCEPT_NAME_MAX_LENGTH}
				showCharCount
				component={ReduxControls.Input}
				validate={[required, conceptNameAllowedChars, conceptNameMaxLength]}
				onChange={() => setApiError(false)}
			/>
			{displayVersions && (
				<Field
					name="shouldCopyVersions"
					label="Include Versions"
					component={ReduxControls.Checkbox}
					useValue
				/>
			)}
		</div>
	);

	const renderContentBulk = () => (
		<div className="duplicate-creative-content bulk-duplicate">
			<ProgressIndicator isLoading={isUpdating} />
			<Field
				name="suffix"
				label="Append to All Duplicated Concept Names"
				component={DuplicateSuffixField}
				props={{ onUndo: setSuffix }}
				className="suffix-container"
			/>
			<hr />
			{concepts.map((concept, i) => (
				<Field
					key={concept.id}
					name={`concepts[${i}].newName`}
					label={concept.name}
					component={ReduxControls.Input}
					type="text"
				/>
			))}
		</div>
	);

	const renderFooter = () => ([
		<Button key="cancel" theme="grey" disabled={isUpdating} onClick={onClose}>Cancel</Button>,
		<Button key="submit" theme="green" disabled={apiError || invalid || isUpdating || isLoadingConcept} onClick={handleSubmit(onSubmit)}>Duplicate</Button>
	]);

	return (
		<Blade
			type="modal"
			setAppTitle={false}
			className="duplicate-creatives"
			title={modalTitle}
			allowFullScreen={false}
			xClose
			bladeProps={{ verticallyCenter: true }}
			onClose={onClose}
			renderContent={isSingle ? renderContentSingle : renderContentBulk}
			renderFooter={renderFooter}
		/>
	);
}

DuplicateConcepts.displayName = "DuplicateConcepts";

export default reduxForm({
	form: DUPLICATE_CONCEPTS_FORM,
	initialValues: {
		suffix: ' copy',
		concepts: [],
		newConceptName: '',
		shouldCopyVersions: false,
	},
})(DuplicateConcepts);

